'use client';

import {
  Box,
  Typography,
  IconButton,
  InputBase,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { Search, Menu as MenuIcon, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import Badge from '@mui/material/Badge';
import { useCart } from '../../cart/CartManager';
import { useWishlist } from '../../contexts/WishlistContext';
import SideMenu from './SideMenu';
import { categoriesApi, Category } from '../../api/categories';
import { getCategoryName } from '../../utils/categoryUtils';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';
import SearchSuggestions from './SearchSuggestions';
import { getMenus, Menu as MenuType } from '../../api/shop';
import { MenuItem as ShopMenuItem } from '../../types/shop';

export default function Header() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [categoriesAnchor, setCategoriesAnchor] = useState<null | HTMLElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [menus, setMenus] = useState<MenuType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cartItemCount, clearCart } = useCart();
  const { wishlistItems } = useWishlist();
  
  // Search suggestions hook
  const { suggestions, loading: suggestionsLoading } = useSearchSuggestions(searchQuery, 300);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    clearCart();
    logout();
    handleProfileMenuClose();
    navigate('/');
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleWishlistClick = () => {
    handleProfileMenuClose();
    navigate('/wishlist');
  };

  const handleCategoriesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCategoriesAnchor(event.currentTarget);
    if (categories.length === 0) {
      fetchCategories();
    }
  };

  const handleCategoriesMenuClose = () => {
    setCategoriesAnchor(null);
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const data = await categoriesApi.getCategories();
      // Filter only top-level categories (parentId is null) and active categories
      const topLevelCategories = data.filter(cat => cat.parentId === null && cat.isActive);
      setCategories(topLevelCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchMenus = async () => {
    try {
      const data = await getMenus();
      setMenus(data);
    } catch (error) {
      console.error('Error fetching menus:', error);
    }
  };

  const handleCategoryClick = (category: Category) => {
    navigate(`/categories/${category.id}/${encodeURIComponent(getCategoryName(category, i18n.language))}`);
    handleCategoriesMenuClose();
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      // Only close search bar for mobile (when opened via icon)
      if (searchBarVisible) {
        setSearchBarVisible(false);
      }
    }
  };

  const handleSuggestionClick = () => {
    setShowSuggestions(false);
    setSearchQuery('');
    // Only close search bar for mobile (when opened via icon)
    if (searchBarVisible) {
      setSearchBarVisible(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleOpenSearchBar = () => {
    setSearchBarVisible(true);
    // Focus the input after the component renders
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleCloseSearchBar = () => {
    setSearchBarVisible(false);
    setSearchQuery('');
    setShowSuggestions(false);
  };



  // Fetch menus on component mount
  useEffect(() => {
    fetchMenus();
  }, []);

  // Listen for side menu open event from mobile bottom navigation
  useEffect(() => {
    const handleOpenSideMenu = () => {
      setSideMenuOpen(true);
    };

    window.addEventListener('openSideMenu', handleOpenSideMenu);
    
    return () => {
      window.removeEventListener('openSideMenu', handleOpenSideMenu);
    };
  }, []);

  // Helper function to extract relative path from full URL
  const extractRelativePath = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      // If it's already a relative path, return as is
      return url.startsWith('/') ? url : `/${url}`;
    }
  };

  // Fixed menu items that are always present and unaffected by API changes
  const fixedMenuItems = [
    { label: t('navigation.categories'), path: '#' },
    { label: t('navigation.new'), path: '/new-products' },
    { label: t('navigation.sale'), path: '/sale-products' },
  ];

  // Mobile menu items (excludes Categories since it's accessible via sidebar)
  const mobileFixedMenuItems = [
    { label: t('navigation.new'), path: '/new-products' },
    { label: t('navigation.sale'), path: '/sale-products' },
  ];

  // Helper function to get the correct label based on current language
  const getMenuItemLabel = (item: ShopMenuItem): string => {
    const currentLang = i18n.language;
    if (currentLang === 'zh' && item.labelZh) {
      return item.labelZh;
    } else if (currentLang === 'cn' && item.labelCn) {
      return item.labelCn;
    }
    return item.label;
  };

  // Get additional menu items from API (excluding fixed items)
  const mainMenu = menus.find(menu => menu.position === 'Main-menu' && menu.isActive);
  const apiNavItems = mainMenu?.items?.map(item => ({
    label: getMenuItemLabel(item),
    path: extractRelativePath(item.link),
  })) || [
    { label: t('navigation.membership'), path: '/page/membership' },
  ];
  
  // Combine fixed items with API items
  const navItems = [
    ...fixedMenuItems,
    ...apiNavItems
  ];

  // Mobile navigation items (combines mobile fixed items with API items)
  const mobileNavItems = [
    ...mobileFixedMenuItems,
    ...apiNavItems
  ];

  return (
    <AppBar
      position="static"
      color="inherit"
      elevation={0}
      sx={{ 
        mt: 1.5,
        position: 'static',
        backgroundColor: 'white'
      }}
    >
      <Box sx={{alignSelf: 'center'}} maxWidth="lg" width={'100%'}>
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            p: '0px !important',
          }}
        >
          {/* Mobile Layout: Categories Menu Icon - Logo - Cart Icon */}
          {/* Categories Menu Icon (Left) - Mobile Only */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', pl: 1 }}>
            <IconButton 
              sx={{ color: '#a98c74' }}
              onClick={() => setSideMenuOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo (Center) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              flex: { xs: 1, md: 'none' },
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
            onClick={() => navigate('/')}
          >
            <img
              src="/images/logo.svg"
              alt="Hatsuhiken 初日妍 | 直送精選日韓藥妝保健 "
              style={{ height: 48 }}
            />
          </Box>
          
          {/* Search Bar - Desktop Only */}
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              flex: 1,
              mx: 4,
              maxWidth: 400,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              border: '1px solid #a98c74',
              borderRadius: 2,
              bgcolor: 'white',
              position: 'relative',
            }}
          >
            <InputBase
              ref={searchInputRef}
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder={t('common.search')}
              sx={{ ml: 2, flex: 1, color: '#a98c74' }}
              inputProps={{ 'aria-label': 'search' }}
            />
            {searchQuery && (
              <IconButton
                onClick={handleClearSearch}
                sx={{ p: '10px', color: '#a98c74' }}
                aria-label="clear search"
              >
                <Close />
              </IconButton>
            )}
            <IconButton
              type="submit"
              sx={{ p: '10px', color: '#a98c74' }}
              aria-label="search"
            >
              <Search />
            </IconButton>
            
            {/* Search Suggestions */}
            <SearchSuggestions
              suggestions={suggestions}
              loading={suggestionsLoading}
              query={searchQuery}
              visible={showSuggestions}
              onSuggestionClick={handleSuggestionClick}
            />
          </Box>

          {/* Desktop Icons - Hidden on Mobile */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            {user ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={handleProfileMenuOpen}
              >
                <img
                  src="/icons/ic_user.svg"
                  alt="Sign In"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'block',
                    marginTop: 4,
                  }}
                />
                <Typography variant="headerIconLabel">
                  {`${user?.firstName} ${user?.lastName}`}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/sign-in')}
              >
                <img
                  src="/icons/ic_user.svg"
                  alt="Sign In"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'block',
                    marginTop: 4,
                  }}
                />
                <Typography variant="headerIconLabel">
                  {t('common.signIn')}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/cart')}
            >
              <Badge badgeContent={cartItemCount} color="error" overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <img
                  src="/icons/ic_cart.svg"
                  alt="Cart"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'block',
                    marginTop: 4,
                  }}
                />
              </Badge>
              <Typography variant="headerIconLabel">
                {t('common.cart')}
              </Typography>
            </Box>
            {/* Language Switcher */}
            <LanguageSwitcher />
          </Box>

          {/* Mobile Icons (Right) - Mobile Only */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 2, pr: 1 }}>
            {/* Mobile Search Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={handleOpenSearchBar}
            >
              <img
                src="/icons/ic_search_brown.svg"
                alt="Search"
                style={{
                  width: 24,
                  height: 24,
                  display: 'block',
                }}
              />
            </Box>
            {/* Mobile Cart Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/cart')} 
            >
              <Badge badgeContent={cartItemCount} color="error" overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <img
                  src="/icons/ic_cart.svg"
                  alt="Cart"
                  style={{
                    width: 28,
                    height: 28,
                    display: 'block',
                  }}
                />
              </Badge>
            </Box>
          </Box>
        </Toolbar>
        {/* Navigation */}
        <Toolbar
          sx={{
            bgcolor: 'white',
            minHeight: 40,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            p: '0px !important',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flex: 1, 
            overflow: 'hidden',
            minWidth: 0, // Allow flex item to shrink
          }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': { display: 'none' }, // Chrome, Safari
                flex: 1,
                px: 1,
                minWidth: 0, // Allow flex item to shrink below content size
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
              }}
            >
              {(isMobile ? mobileNavItems : navItems).map(item => (
                <Typography
                  key={item.label}
                  variant="navMenu"
                  sx={{
                    color: theme.palette.custom.brown,
                    fontWeight: 500,
                    fontSize: '16px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    cursor: 'pointer',
                    minWidth: 'fit-content',
                    px: 1, // Add horizontal padding
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                  onClick={item.label === t('navigation.categories') && !isMobile ? handleCategoriesMenuOpen : () => item.path !== '#' && navigate(item.path)}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          </Box>

        </Toolbar>

      </Box>
      {/* Profile Menu */}
      {user && (
        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={handleProfileClick}>{t('navigation.profile')}</MenuItem>
          <MenuItem onClick={handleWishlistClick}>
            {t('navigation.myWishlist')} ({wishlistItems.length})
          </MenuItem>
          <MenuItem onClick={handleLogout}>{t('navigation.logout')}</MenuItem>
        </Menu>
      )}

      {/* Categories Dropdown Menu - Desktop Only */}
      {!isMobile && (
        <Menu
          anchorEl={categoriesAnchor}
          open={Boolean(categoriesAnchor)}
          onClose={handleCategoriesMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 400,
              maxHeight: 500,
              overflow: 'auto',
            },
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                mb: 2,
              }}
            >
              {t('navigation.categories')}
            </Typography>
            
            {categoriesLoading ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {[...Array(8)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="text"
                    width="100%"
                    height={32}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 1,
                maxHeight: 300,
                overflow: 'auto'
              }}>
                {categories.map((category) => (
                  <Box
                    key={category.id}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid transparent',
                      '&:hover': {
                        border: '1px solid #e0e0e0',
                        bgcolor: '#f5f5f5'
                      }
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#333',
                        textAlign: 'left',
                        lineHeight: 1.2,
                      }}
                    >
                      {getCategoryName(category, i18n.language)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Menu>
      )}
      
      {/* Side Menu */}
      <SideMenu 
        open={sideMenuOpen} 
        onClose={() => setSideMenuOpen(false)} 
      />

      {/* Search Bar - Appears at top when search icon is clicked */}
      {searchBarVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: 'white',
            borderBottom: '1px solid #E7E3D5',
            px: 2,
            py: 1,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              mx: 'auto',
              border: '1px solid #a98c74',
              borderRadius: 2,
              bgcolor: 'white',
              position: 'relative',
            }}
          >
            <InputBase
              ref={searchInputRef}
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder={t('common.search')}
              sx={{ ml: 2, flex: 1, color: '#a98c74' }}
              inputProps={{ 'aria-label': 'search' }}
            />
            <IconButton
              type="submit"
              sx={{ p: '10px', color: '#a98c74' }}
              aria-label="search"
            >
              <Search />
            </IconButton>
            <IconButton
              onClick={handleCloseSearchBar}
              sx={{ p: '10px', color: '#a98c74' }}
              aria-label="close search"
            >
              <Close />
            </IconButton>
          </Box>
          
          {/* Search Suggestions */}
          <SearchSuggestions
            suggestions={suggestions}
            loading={suggestionsLoading}
            query={searchQuery}
            visible={showSuggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        </Box>
      )}
    </AppBar>
  );
}
